/**
 * Write outbox — queues mutations when offline; replays them when online.
 *
 * Ordering guarantee: mutations for the same lead are stored with a
 * monotonically increasing `sequence` so they are replayed in FIFO order.
 * The `idempotencyKey` (UUID v4) is sent as the `Idempotency-Key` header,
 * which the backend uses to deduplicate retries.
 *
 * Usage:
 *   const { enqueue, flush, pendingCount } = useOutbox()
 *
 *   // Optimistically update local state, then enqueue
 *   await enqueue({
 *     leadId: 42,
 *     method: 'PATCH',
 *     path: '/calls/7',
 *     body: { disposition: 'NOT_INTERESTED' },
 *   })
 */

import { v4 as uuidv4 } from 'uuid'
import { useState, useEffect, useCallback } from 'react'
import { offlineDB, nextOutboxSequence, type OutboxEntry, type OutboxMethod } from './db'
import api from './api'

// ── Core enqueue ──────────────────────────────────────────────────────────────

interface EnqueueParams {
  leadId: number
  method: OutboxMethod
  path: string
  body: unknown
}

/**
 * Add a mutation to the outbox.
 * Reads userId/tenantId from the current auth token stored in localStorage.
 */
export async function enqueueWrite(params: EnqueueParams): Promise<string> {
  const raw = localStorage.getItem('user')
  const user = raw && raw !== 'undefined' ? JSON.parse(raw) : {}
  const userId: number = user.id ?? 0
  const tenantId: number = user.tenant_id ?? 0
  const idempotencyKey = uuidv4()
  const sequence = await nextOutboxSequence(params.leadId)

  await offlineDB.outbox.add({
    userId,
    tenantId,
    idempotencyKey,
    leadId: params.leadId,
    sequence,
    method: params.method,
    path: params.path,
    body: params.body,
    enqueuedAt: new Date().toISOString(),
    retryCount: 0,
  })

  return idempotencyKey
}

// ── Flush ─────────────────────────────────────────────────────────────────────

/**
 * Replay all pending outbox entries in FIFO order (sorted by id, which is
 * auto-incremented). Entries for the same lead are guaranteed to be in
 * sequence order because nextOutboxSequence() is called synchronously per lead.
 *
 * On success: deletes the entry.
 * On 4xx (non-idempotent error): deletes the entry (no point retrying).
 * On 5xx / network error: increments retryCount, leaves in outbox.
 *
 * @returns number of entries successfully flushed
 */
export async function flushOutbox(): Promise<number> {
  const raw = localStorage.getItem('user')
  const user = raw && raw !== 'undefined' ? JSON.parse(raw) : {}
  const userId: number = user.id ?? 0

  // Load all entries for this user, ordered by auto-id (FIFO globally)
  const entries = await offlineDB.outbox
    .where('userId').equals(userId)
    .sortBy('id') as OutboxEntry[]

  let flushed = 0

  for (const entry of entries) {
    try {
      await api.request({
        method: entry.method,
        url: entry.path,
        data: entry.body,
        headers: { 'Idempotency-Key': entry.idempotencyKey },
      })
      if (entry.id != null) await offlineDB.outbox.delete(entry.id)
      flushed++
    } catch (err: any) {
      const status: number | undefined = err?.response?.status
      try {
        if (status && status >= 400 && status < 500) {
          // Client error — not retryable; drop the entry
          if (entry.id != null) await offlineDB.outbox.delete(entry.id)
        } else {
          // Network or server error — increment retry count
          if (entry.id != null) await offlineDB.outbox.update(entry.id, {
            retryCount: (entry.retryCount ?? 0) + 1,
          })
        }
      } catch (dbErr) {
        console.warn('[Outbox] DB update failed for entry', entry.id, dbErr)
      }
    }
  }

  return flushed
}

// ── React hook ────────────────────────────────────────────────────────────────

/**
 * Hook that exposes enqueue + flush and keeps a live count of pending entries.
 */
export function useOutbox() {
  const [pendingCount, setPendingCount] = useState(0)

  const refreshCount = useCallback(async () => {
    const raw = localStorage.getItem('user')
    const user = raw && raw !== 'undefined' ? JSON.parse(raw) : {}
    const userId: number = user.id ?? 0
    const count = await offlineDB.outbox.where('userId').equals(userId).count()
    setPendingCount(count)
  }, [])

  useEffect(() => {
    refreshCount()
    // Re-count whenever the outbox table changes
    offlineDB.outbox.hook('creating', refreshCount)
    offlineDB.outbox.hook('deleting', refreshCount)
    return () => {
      offlineDB.outbox.hook('creating').unsubscribe(refreshCount)
      offlineDB.outbox.hook('deleting').unsubscribe(refreshCount)
    }
  }, [refreshCount])

  const enqueue = useCallback(async (params: EnqueueParams) => {
    const key = await enqueueWrite(params)
    await refreshCount()
    return key
  }, [refreshCount])

  const flush = useCallback(async () => {
    const n = await flushOutbox()
    await refreshCount()
    return n
  }, [refreshCount])

  return { enqueue, flush, pendingCount }
}
